/**
 * PRESS FEEDBACK — the one thing that makes a control feel like an object.
 *
 * `docs/UI-STYLE.md`: "On press, scale to 95% and spring back on release. This
 * is the single cue that makes a UI feel physical, and a button without it
 * reads as a picture of a button."
 *
 * ── WHY THIS IS A FUNCTION AND NOT A CSS CLASS ──────────────────────────────
 * `:active` is nearly right and fails in the two places that matter. It does
 * not survive the pointer leaving the element while held (the press sticks
 * visually until release, anywhere on the page), and it does not fire at all
 * for a pointer cancelled by a scroll or a system gesture — which on a phone is
 * most aborted presses. Both leave a button shrunk and looking broken.
 *
 * ── THE TRAP THIS PROJECT HAS HIT TWICE ─────────────────────────────────────
 * A control drawn over the match canvas can be visually perfect and completely
 * dead: `PointerInput` preventDefaults its pointerdown, which suppresses the
 * `click` the browser would otherwise synthesise, and jsdom dispatches
 * SYNTHETIC clicks, so every unit test passes. The coach's "Continue" shipped
 * dead for exactly this reason.
 *
 * So this listens for `pointerup` ON THE ELEMENT and invokes the action itself
 * rather than waiting for `click`. `click` is still wired for keyboard and
 * assistive technology, and `guard` stops the two paths double-firing.
 */
const PRESSED = 'is-pressed';
const RELEASED = 'is-released';
/** Long enough for the 260ms release keyframes in button.ts to finish. */
const RELEASE_MS = 320;

export interface PressOptions {
  /** Runs once per activation, whether by pointer, keyboard or AT. */
  readonly onPress: () => void;
  /** Skip the scale cue; the action still fires. Respects reduced motion. */
  readonly reducedMotion?: boolean;
  /**
   * Take pointer capture on press (default true). FALSE for a control that is
   * also a DRAG SOURCE owned by someone else — the match hand's card slots,
   * whose drag `PointerInput` captures on the board host. Two captures cannot
   * coexist: the host's would steal the release and leave this one stuck
   * pressed. Without capture, leaving the element cancels the press, which is
   * exactly right for a card being dragged out of its slot.
   */
  readonly capture?: boolean;
  /**
   * Treat `aria-disabled="true"` as inert (default true). FALSE where the
   * attribute ANNOUNCES a state rather than forbids the press: the match
   * hand marks an unaffordable card `aria-disabled` so a screen reader says
   * so, yet pressing it still selects it (the deploy controller answers with
   * its own refusal cue). The real `disabled` attribute always blocks.
   */
  readonly respectAriaDisabled?: boolean;
}

/** Attach press feedback + activation. Returns a teardown. */
export function attachPress(node: HTMLElement, options: PressOptions): () => void {
  let held = false;
  // Set by the pointer path so the `click` that follows it is ignored. Cleared
  // on a microtask, which is after the click but before any later real one.
  let handledByPointer = false;

  let releaseTimer: ReturnType<typeof setTimeout> | null = null;

  const setPressed = (on: boolean): void => {
    if (options.reducedMotion === true) return;
    const was = node.classList.contains(PRESSED);
    node.classList.toggle(PRESSED, on);
    // THE RELEASE SPRING. A skinned button (.rga-btn in button.ts) plays a
    // short overshoot on the CSS scale property when this class lands; other
    // controls define no rule for it and ignore it. Re-adding restarts the
    // keyframes, so a fast double tap springs twice rather than once.
    if (on) {
      node.classList.remove(RELEASED);
    } else if (was) {
      node.classList.remove(RELEASED);
      void node.offsetWidth;
      node.classList.add(RELEASED);
      if (releaseTimer !== null) clearTimeout(releaseTimer);
      releaseTimer = setTimeout(() => {
        releaseTimer = null;
        node.classList.remove(RELEASED);
      }, RELEASE_MS);
    }
  };

  const isDisabled = (): boolean =>
    node.hasAttribute('disabled') ||
    (options.respectAriaDisabled !== false && node.getAttribute('aria-disabled') === 'true');

  const onDown = (e: PointerEvent): void => {
    if (isDisabled() || e.button !== 0) return;
    held = true;
    setPressed(true);
    // Keeps the release ours even if the finger slides off the element, so a
    // press cannot end stuck-down somewhere else.
    if (options.capture !== false && typeof node.setPointerCapture === 'function') {
      try {
        node.setPointerCapture(e.pointerId);
      } catch {
        /* pointer already gone; the up handler still runs */
      }
    }
  };

  const onUp = (e: PointerEvent): void => {
    if (!held) return;
    held = false;
    setPressed(false);
    if (isDisabled()) return;
    // Only a release INSIDE the element activates — dragging off and letting go
    // is how a player cancels, and every platform behaves this way.
    const r = node.getBoundingClientRect();
    const inside =
      e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
    if (!inside) return;
    handledByPointer = true;
    void Promise.resolve().then(() => {
      handledByPointer = false;
    });
    options.onPress();
  };

  const onCancel = (): void => {
    held = false;
    setPressed(false);
  };

  const onClick = (e: MouseEvent): void => {
    // Keyboard and assistive technology only; the pointer path already ran.
    //
    // `handledByPointer` alone is NOT enough, and jsdom cannot show you why: it
    // dispatches the synthetic click synchronously, so the microtask guard is
    // still up. A real browser dispatches the click in a LATER task, by which
    // time the guard has cleared — so every press activated TWICE in Chrome
    // while passing every unit test. Worse, `setPointerCapture` retargets the
    // click to this element even when the finger was released somewhere else,
    // so a drag-off — the platform gesture for CANCEL — activated anyway.
    //
    // `detail` separates them for good: a click produced by a pointer carries
    // its click count (>= 1), while a click synthesised by a keyboard or by
    // assistive technology carries 0. So a pointer-born click is always the
    // pointer path's business, and it has already decided.
    if (e.detail > 0) return;
    if (handledByPointer || isDisabled()) return;
    options.onPress();
  };

  node.addEventListener('pointerdown', onDown);
  node.addEventListener('pointerup', onUp);
  node.addEventListener('pointercancel', onCancel);
  node.addEventListener('click', onClick as EventListener);
  // Uncaptured, a release elsewhere never reaches this node: leaving it IS
  // the cancel (and the drag-out of a card slot).
  const uncaptured = options.capture === false;
  if (uncaptured) node.addEventListener('pointerleave', onCancel);

  return () => {
    node.removeEventListener('pointerdown', onDown);
    node.removeEventListener('pointerup', onUp);
    node.removeEventListener('pointercancel', onCancel);
    if (uncaptured) node.removeEventListener('pointerleave', onCancel);
    node.removeEventListener('click', onClick as EventListener);
    if (releaseTimer !== null) clearTimeout(releaseTimer);
    node.classList.remove(PRESSED, RELEASED);
  };
}

/**
 * ── THE SHARED TRANSFORM CONTRACT ───────────────────────────────────────────
 * THE COLLISION THIS EXISTS TO PREVENT (`_errata/visual-volume.md` E2, and
 * `_CONTRACTS.md` §6.5 rule 2). This sheet used to say
 *
 *     .rga-press.is-pressed{transform:scale(.95)}
 *
 * and `controls.ts` said `.rga-plate:active{transform:translateY(3px)}`. Both
 * are UNLAYERED, both are specificity (0,2,0), and `styles.ts` appends
 * CONTROLS_CSS after PRESS_CSS — so on any element carrying both classes (which
 * is every plated button) the plate's `transform` won on source order and the
 * 95 % press cue silently stopped existing. `docs/UI-STYLE.md`'s "scale to 95 %
 * and spring back — every button, no exceptions" became false product-wide, and
 * NOTHING could see it: jsdom computes no cascade, so every test stayed green.
 *
 * The fix is that NO STATE RULE OWNS `transform` ANY MORE. Exactly one rule per
 * element composes it, out of two custom properties, and every state — press,
 * hover, disabled — writes a VARIABLE instead of the property:
 *
 *     --rga-press-y   vertical travel   (negative = up, positive = sunk)
 *     --rga-press-s   scale factor
 *
 * Two rules setting two different custom properties cannot beat each other, so
 * the plate sinks 3 px AND shrinks 5 % at the same time. `controls.ts` writes
 * the same two names; they are the contract between the two files, and
 * `test/ui-motion.test.ts` resolves the real cascade to prove both survive.
 *
 * The base rule RESETS both variables rather than relying on the `var()`
 * fallback, because custom properties inherit: without the reset a pressed
 * plate would hand `--rga-press-s:.95` to a nested `.rga-press` child and the
 * whole subtree would shrink with it.
 *
 * ── THE TIMINGS ─────────────────────────────────────────────────────────────
 * 90 ms out and 140 ms back is deliberately asymmetric: the shrink has to feel
 * instant to read as a response to the finger, the return can afford to be
 * seen. The overshoot on the way back is what makes it read as sprung rather
 * than as a fade. Both are pinned by `VISUAL-VOLUME-AND-MOTION.md` §3.1.
 *
 * ── HOVER, FOCUS, DISABLED ──────────────────────────────────────────────────
 * HOVER is a 1 px rise — the smallest travel that still reads, and the opposite
 * direction to the press, so hovering and pressing are never confusable. It is
 * behind `(hover:hover)` because a touch device latches `:hover` after a tap
 * and would leave the button hovering until something else was touched, and
 * behind `(prefers-reduced-motion:no-preference)` because a rise on pointer
 * proximity is decoration, not feedback — it is the first thing to go.
 *
 * The hover rule excludes the pressed and active states IN ITS OWN SELECTOR
 * rather than relying on order or specificity. `:not()` chains raise
 * specificity, so a hover rule written the obvious way (0,6,0) would have
 * outranked `.rga-plate:active` (0,2,0) and the button would have risen while
 * being held down — with a mouse, that is every press.
 *
 * FOCUS does NOT move. A focus ring that shifts its own target is hostile to
 * the keyboard user following it; the ring is the whole cue. It is declared
 * here, unlayered, so it beats the layered Tailwind `focus-visible:outline-*`
 * utilities the call sites carry and every control focuses identically.
 *
 * DISABLED is four channels and not one of them is colour: the `disabled`
 * attribute (semantics), reduced opacity, `cursor:not-allowed`, and the total
 * absence of movement — hover and press both zero out. STYLE_BIBLE §3.
 *
 * ── REDUCED MOTION ──────────────────────────────────────────────────────────
 * The hover rise disappears. The press cue does NOT: it collapses to an instant
 * change of state instead of a spring. Removing it entirely (which this sheet
 * used to do, with `transform:none`) leaves a reduced-motion player pressing
 * buttons that never acknowledge the press — "a picture of a button", the exact
 * failure `docs/UI-STYLE.md` names — and feedback on activation is not the
 * "non-essential animation" WCAG 2.3.3 is about. `.01ms` rather than `0`
 * matches `a11y/apply.ts`'s in-app kill switch, which uses that value because a
 * zero-duration animation never fires `animationend`; the two paths now agree
 * instead of one dropping the cue and the other keeping it.
 */
export const PRESS_CSS = `
.rga-press{--rga-press-y:0px;--rga-press-s:1;
  transform:translate3d(0,var(--rga-press-y,0px),0) scale(var(--rga-press-s,1));
  transition:transform 140ms cubic-bezier(.34,1.56,.64,1),opacity 140ms linear}
.rga-press.is-pressed{transition-duration:90ms;--rga-press-s:.95}
@media (hover:hover) and (prefers-reduced-motion:no-preference){
.rga-press:hover:not(.is-pressed):not(:active):not([disabled]):not([aria-disabled=true]){--rga-press-y:-1px}
}
.rga-press:focus-visible{outline:2px solid var(--color-gold);outline-offset:2px}
.rga-press[disabled],.rga-press[aria-disabled=true]{opacity:.55;cursor:not-allowed;
  --rga-press-y:0px;--rga-press-s:1}
@media (prefers-reduced-motion:reduce){.rga-press,.rga-press.is-pressed{transition-duration:.01ms}}
`;
